/*
 * Selo "Instagram - ao vivo" (sections/instagram-live-badge.liquid).
 *
 *  - mostra o selo sozinho a partir da data/hora de início (data-start)
 *  - esconde sozinho no término (data-end), mesmo com a página já aberta
 *  - ao aparecer mostra a dica (balão) e a esconde após data-collapse-after ms (0 = não esconde)
 *  - botão de fechar, lembrado por sessionStorage (data-storage-key)
 *  - fica encostado acima da pilha de botões flutuantes (snippets/buttons-floating.liquid) e
 *    acompanha a altura dela (o "voltar ao topo" só aparece depois de rolar a página)
 */
(function () {
  const MAX_TIMEOUT = 2147483647; // limite do setTimeout (~24,8 dias)
  const STACK_SELECTOR = '.box-content .box-opcoes-flutuantes';
  const STACK_GAP = 10; // mesmo gap entre os botões flutuantes

  class InstagramLiveBadge extends HTMLElement {
    constructor() {
      super();
      this.schedule = this.schedule.bind(this);
      this.onClose = this.onClose.bind(this);
      this.dock = this.dock.bind(this);
    }

    connectedCallback() {
      this.startDate = this.dataset.start ? Date.parse(this.dataset.start) : NaN;
      this.endDate = this.dataset.end ? Date.parse(this.dataset.end) : NaN;
      this.collapseAfter = parseInt(this.dataset.collapseAfter, 10) || 0;
      this.storageKey = this.dataset.storageKey || '';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this.closeButton = this.querySelector('[data-live-close]');
      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.onClose);
      }

      if (this.isDismissed()) {
        this.hidden = true;
        return;
      }

      this.observeStack();
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.collapseTimer);
      if (this.closeButton) {
        this.closeButton.removeEventListener('click', this.onClose);
      }
      if (this.stackObserver) this.stackObserver.disconnect();
      window.removeEventListener('resize', this.dock);
    }

    // Posiciona o selo logo acima da pilha de botões flutuantes e reposiciona quando ela muda de altura.
    observeStack() {
      this.stack = document.querySelector(STACK_SELECTOR);
      if (!this.stack) return; // botões flutuantes desativados: usa a distância da configuração

      if ('ResizeObserver' in window) {
        this.stackObserver = new ResizeObserver(this.dock);
        this.stackObserver.observe(this.stack);
      }
      window.addEventListener('resize', this.dock);
      this.dock();
    }

    dock() {
      if (!this.stack) return;
      const rect = this.stack.getBoundingClientRect();
      if (!rect.height) {
        this.style.removeProperty('--live-bottom');
        return;
      }
      const bottom = Math.round(window.innerHeight - rect.top + STACK_GAP);
      this.style.setProperty('--live-bottom', `${bottom}px`);
    }

    // Decide se está antes, durante ou depois da live e agenda a próxima virada.
    schedule() {
      clearTimeout(this.timer);

      if (this.designMode) {
        this.show();
        return;
      }

      const now = Date.now();

      if (!isNaN(this.endDate) && now >= this.endDate) {
        this.hide();
        return;
      }

      if (!isNaN(this.startDate) && now < this.startDate) {
        // Página pode ter sido cacheada antes do início: garante que fica oculta até lá.
        this.hidden = true;
        this.timer = setTimeout(this.schedule, Math.min(this.startDate - now, MAX_TIMEOUT));
        return;
      }

      this.show();

      if (!isNaN(this.endDate)) {
        this.timer = setTimeout(this.schedule, Math.min(this.endDate - now, MAX_TIMEOUT));
      }
    }

    show() {
      if (this.classList.contains('is-visible')) return;

      this.hidden = false;
      this.classList.remove('is-collapsed');

      // Dois frames para a transição de entrada rodar depois do display:none sair.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('is-visible'));
      });

      clearTimeout(this.collapseTimer);
      if (this.collapseAfter > 0) {
        this.collapseTimer = setTimeout(() => this.classList.add('is-collapsed'), this.collapseAfter);
      }
    }

    hide() {
      clearTimeout(this.collapseTimer);
      this.classList.remove('is-visible');
      this.hidden = true;
    }

    isDismissed() {
      if (this.designMode || !this.storageKey) return false;
      try {
        return window.sessionStorage.getItem(this.storageKey) === '1';
      } catch (e) {
        return false;
      }
    }

    onClose(event) {
      event.preventDefault();
      event.stopPropagation();
      try {
        if (this.storageKey) window.sessionStorage.setItem(this.storageKey, '1');
      } catch (e) {
        // sessionStorage indisponível (modo privado etc.): só esconde nesta página.
      }
      this.hide();
    }
  }

  if (!customElements.get('instagram-live-badge')) {
    customElements.define('instagram-live-badge', InstagramLiveBadge);
  }
})();
